import { LIMIT_SECS, WARN_SECS, type CallState, type NoteKey, type Turn } from "./types";

/**
 * The real call.
 *
 * The page asks our own server for a short-lived key, then talks WebRTC
 * straight to OpenAI: the audio never passes through anything of ours. What
 * comes back down the data channel is turned into the same four fields the
 * scripted call used to drive, so the screens above this file do not know the
 * difference.
 *
 * The shape is `simulate`'s, deliberately: start it, it drives the state, and
 * the one function it returns stops everything it owns. It is write-only
 * against that state — everything it needs to remember it keeps in its own
 * closure, and every patch goes through `put`, which is the one place the
 * call's being over is checked.
 */

type Patch = Partial<CallState> | ((s: CallState) => Partial<CallState>);

const TOKEN_URL = "/api/call/token";
const TRANSCRIPT_URL = "/api/call/transcript";
const CALLS_URL = "https://api.openai.com/v1/realtime/calls";

/** Pressed the button, and nothing has come back: give up and say so. */
const CONNECT_MS = 15_000;

export function connect(set: (patch: Patch) => void): () => void {
  // Everything below is guarded on this. A call that has been hung up, has run
  // out its three minutes, or has failed is over, and a continuation that
  // arrives afterwards must not push anything into it.
  let dead = false;
  let timers: ReturnType<typeof setTimeout>[] = [];
  const abort = new AbortController();
  let mic: MediaStream | null = null;
  let pc: RTCPeerConnection | null = null;
  let chan: RTCDataChannel | null = null;
  let audio: HTMLAudioElement | null = null;

  // What she opens and closes with, handed back with the key.
  let greeting = "";
  let signoff = "";

  // This call's id, handed back with the key, and the words as they are said.
  //
  // The transcript is kept, and this is the only place it exists: the audio
  // goes browser-to-OpenAI and nothing of ours is in that path, so if the page
  // does not hand the words over when the call ends, nobody ever sees them.
  // Mirrored here rather than read back off the provider because the provider
  // resets to `INITIAL` the moment the call is hung up, which is the same
  // instant this has to read them.
  let call = "";
  let said: Turn[] = [];
  let kept = false;

  // The session exists: what separates "never got up" from "dropped".
  let began = false;
  // She has asked to hang up and is saying her last line.
  let ending = false;
  // Whether the far end is telling us when her audio actually starts and
  // stops, rather than when generating it does.
  let buffered = false;
  // The response whose words are growing in the last turn, whether that turn
  // is still hers to grow, and the words not yet drawn.
  let saying: string | null = null;
  let open = false;
  let buf = "";
  let frame = 0;

  const later = (ms: number, f: () => void) => timers.push(setTimeout(f, ms));

  /** Deliberately safe to call twice, and after something arrived late. */
  const release = () => {
    timers.forEach(clearTimeout);
    timers = [];
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    buf = "";
    abort.abort();
    mic?.getTracks().forEach((t) => t.stop());
    mic = null;
    if (chan) {
      chan.onopen = chan.onmessage = chan.onclose = chan.onerror = null;
      chan.close();
      chan = null;
    }
    if (pc) {
      pc.ontrack = pc.onconnectionstatechange = null;
      pc.close();
      pc = null;
    }
    if (audio) {
      audio.pause();
      audio.srcObject = null;
      audio.remove();
      audio = null;
    }
  };

  /**
   * Hand the words over, once, however the call ended.
   *
   * `keepalive` rather than the call's own `AbortController`: every way out of
   * here runs `release`, which aborts that signal, and one of the ways out is
   * the page being navigated away from. A send that is not waited on and
   * cannot be cancelled is the only shape that survives both.
   *
   * Nothing is reported when it fails. The visitor was not asked to send this
   * and has nothing to do about it, so a note on the screen would be the page
   * telling them about our own bookkeeping.
   */
  const keep = () => {
    if (kept || !call || !said.some((t) => t.who === "you")) return;
    kept = true;
    try {
      void fetch(TRANSCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: call, turns: said }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Same as a failed send: there is nothing to do and nobody to tell.
    }
  };

  const stop = () => {
    dead = true;
    keep();
    try {
      release();
    } catch {
      // Teardown must not throw: the provider nulls its handle straight after
      // calling this, and a throw would leave a dead call installed as the
      // live one.
    }
  };

  const put = (patch: Patch) => {
    if (!dead) set(patch);
  };

  /** One way out: say what happened, then take the call down. */
  const done = (patch: Partial<CallState>) => {
    if (dead) return;
    set(patch);
    stop();
  };
  const fail = (note: NoteKey) => done({ phase: "ready", mode: "listening", note });

  const send = (msg: unknown) => {
    if (chan?.readyState === "open") chan.send(JSON.stringify(msg));
  };

  /**
   * Her words, a frame at a time.
   *
   * Deltas arrive faster than the page can usefully draw them, and every one
   * of them re-measures the transcript. Holding them to one patch a frame
   * costs nothing anyone can see.
   */
  const draw = () => {
    frame = 0;
    const chunk = buf;
    buf = "";
    if (dead || !chunk) return;
    const fresh = !open;
    open = true;
    put((s) => {
      const turns = [...s.turns];
      const last = turns[turns.length - 1];
      if (fresh || !last || last.who !== "her") turns.push({ who: "her", text: chunk });
      else turns[turns.length - 1] = { who: "her", text: last.text + chunk };
      said = turns;
      return { mode: "talking", turns };
    });
  };

  /**
   * She has asked to hang up.
   *
   * The words are ours, not hers — a call that tails off into silence with
   * the button still red is not a call that ended. She says the line, and the
   * moment the speaker stops the call is over and the screen says so.
   */
  const farewell = (callId: string) => {
    if (dead || ending) return;
    ending = true;
    send({
      type: "conversation.item.create",
      item: { type: "function_call_output", call_id: callId, output: '{"ok":true}' },
    });
    send({ type: "response.create", response: { instructions: `Say exactly: ${JSON.stringify(signoff)}` } });
    // If her last line never lands, the call still ends.
    later(15_000, () => done({ phase: "ready", note: "end" }));
  };

  const live = () => {
    if (dead || began) return;
    began = true;
    // The watchdog has done its job; the clock the visitor is spending starts
    // here, at the same instant the screen says the call is live.
    timers.forEach(clearTimeout);
    timers = [];
    set({ phase: "live", mode: "listening" });
    // Asked for word for word rather than left to the prompt, the way the
    // phone receptionist's greeting is: with no dialog in front of it, this
    // line is also where the caller is told nothing here gets booked.
    if (greeting) {
      send({ type: "response.create", response: { instructions: `Say exactly: ${JSON.stringify(greeting)}` } });
    }
    later(WARN_SECS * 1000, () => put({ note: "warn" }));
    later(LIMIT_SECS * 1000, () => done({ phase: "ready", note: "end" }));
  };

  const heard = (raw: string) => {
    let ev: {
      type?: string;
      delta?: string;
      transcript?: string;
      response_id?: string;
      item?: { type?: string; name?: string; call_id?: string };
    };
    try {
      ev = JSON.parse(raw);
    } catch {
      return;
    }

    switch (ev.type) {
      // The session is up. Not the data channel opening — that only proves
      // the transport came up, and a key the far end refuses would leave the
      // visitor's three minutes draining on a call that never happened.
      case "session.created":
        live();
        return;

      // The only tool she has: hang up.
      case "response.output_item.done":
        if (ev.item?.type === "function_call" && ev.item.name === "end_call") {
          farewell(ev.item.call_id ?? "");
        }
        return;

      // Hers come in as she says them, growing the last turn in place.
      case "response.output_audio_transcript.delta": {
        const text = ev.delta ?? "";
        if (!text) return;
        const id = ev.response_id ?? "";
        if (saying !== id) {
          if (buf) draw();
          saying = id;
          open = false;
        }
        buf += text;
        if (!frame) frame = requestAnimationFrame(draw);
        return;
      }

      // She has finished, or the caller cut her off and the response was
      // cancelled — either way what she said stands and she is listening. Her
      // turn is only ever what the deltas carried, never the transcript the
      // response ends with: that one holds the tail she was cut off before
      // saying, and putting words in her mouth is worse than stopping short.
      case "response.done":
        if (buf) draw();
        saying = null;
        open = false;
        // The model finishes generating a second or two before the speaker
        // finishes playing, so where the audio buffer reports for itself,
        // that is the one to believe.
        if (!buffered) put({ mode: "listening" });
        return;

      // Her voice starting and stopping, as heard rather than as written.
      case "output_audio_buffer.started":
        buffered = true;
        put({ mode: "talking" });
        return;
      case "output_audio_buffer.stopped":
      case "output_audio_buffer.cleared":
        buffered = true;
        // Her last line has finished playing: that is the call ending, and
        // the speaker going quiet is the moment to say so.
        if (ending) return done({ phase: "ready", note: "end" });
        put({ mode: "listening" });
        return;

      // The caller talking over her is her stopping, now, rather than when
      // the cancelled response is acknowledged.
      case "input_audio_buffer.speech_started":
        put({ mode: "listening" });
        return;

      // The caller's words land whole, once they have finished saying them —
      // and above her reply if she has already started it, because
      // transcription lands late and the order they were said in is the order
      // to read them in.
      case "conversation.item.input_audio_transcription.completed": {
        const text = ev.transcript?.trim();
        if (!text) return;
        const under = open;
        put((s) => {
          const turns = [...s.turns];
          const i = turns.length - 1;
          const at = under && i >= 0 && turns[i]!.who === "her" ? i : turns.length;
          turns.splice(at, 0, { who: "you", text });
          said = turns;
          return { turns };
        });
        return;
      }

      // Before the session exists this is the call failing — a refused key
      // arrives here. After it, the channel being open is proof the call is
      // alive, and one bad turn is something the caller talks through.
      case "error":
        if (!began) fail("failed");
        else console.warn("call: %s", raw.slice(0, 300));
        return;
    }
  };

  // Armed before anything else, and cleared when the session comes up: one
  // timer for every way the call can get stuck rather than break.
  later(CONNECT_MS, () => fail("failed"));

  void (async () => {
    // 1. The microphone, before the key — a visitor who says no should not
    //    have spent one of their calls for the day. The browser's own prompt
    //    is the only gate on this call, so the ways it can be refused are
    //    three different notes.
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as DOMException)?.name;
      if (dead) return release();
      if (name === "NotFoundError" || name === "OverconstrainedError") return fail("nomic");
      if (name === "NotAllowedError" || name === "SecurityError") return fail("blocked");
      // A microphone that exists and will not open — held by something else,
      // or broken. "Try again in a moment" is the truer line.
      return fail("failed");
    }
    if (dead) return release();

    // 2. The key, and with it her first and last lines.
    let key = "";
    try {
      const res = await fetch(TOKEN_URL, { method: "POST", signal: abort.signal });
      if (dead) return release();
      if (res.status === 429) return done({ phase: "limit", note: "limit" });
      if (!res.ok) return fail("failed");
      const body = (await res.json()) as {
        value?: string;
        greeting?: string;
        closing?: string;
        id?: string;
      };
      key = body.value ?? "";
      greeting = body.greeting ?? "";
      signoff = body.closing ?? "";
      call = body.id ?? "";
      if (!key) return fail("failed");
    } catch {
      if (dead) return release();
      return fail("failed");
    }
    if (dead) return release();

    // 3. The call itself. No iceServers: OpenAI answers on host candidates
    //    with a 443 fallback, so there is nothing to configure and a TURN
    //    server of ours would never be used. The offer goes up without
    //    waiting for gathering to finish for the same reason — this is one
    //    exchange with nowhere to trickle to, and the pair forms against the
    //    candidate in the answer.
    try {
      pc = new RTCPeerConnection();

      audio = document.createElement("audio");
      audio.autoplay = true;
      audio.hidden = true;
      document.body.appendChild(audio);
      pc.ontrack = (e) => {
        if (!audio) return;
        audio.srcObject = e.streams[0] ?? null;
        void audio.play().catch(() => {});
      };

      for (const track of mic.getTracks()) pc.addTrack(track, mic);

      chan = pc.createDataChannel("oai-events");
      chan.onmessage = (e) => heard(String(e.data));
      chan.onclose = () => fail("failed");
      chan.onerror = () => fail("failed");

      pc.onconnectionstatechange = () => {
        if (pc?.connectionState === "failed") fail("failed");
      };

      await pc.setLocalDescription(await pc.createOffer());
      if (dead) return release();

      const answer = await fetch(CALLS_URL, {
        method: "POST",
        body: pc.localDescription?.sdp ?? "",
        headers: { Authorization: `Bearer ${key}`, "Content-Type": "application/sdp" },
        signal: abort.signal,
      });
      if (dead) return release();
      if (!answer.ok) return fail("failed");

      await pc.setRemoteDescription({ type: "answer", sdp: await answer.text() });
      if (dead) return release();
    } catch {
      if (dead) return release();
      return fail("failed");
    }
  })();

  return stop;
}
